/**
 * The day grid, as data: which positions make its rows, and what each slot
 * in a row is, read off `/offered` and `/holdings`. Pure, so it is tested
 * without a browser.
 *
 * A `temporal-hour` World's rows are days and its slots hours; a
 * `temporal-minute` World's rows are the hours of one day and its slots
 * minutes. Either way a row is one position and a cell is one slot there.
 */

import type { At, Holding } from './api';

export interface Row {
  readonly at: At;
  readonly label: string;
}

/** `YYYY-MM-DD` plus `n` days, in UTC like the API. */
export const addDays = (date: string, n: number): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

export const today = (now: number = Date.now()): string => new Date(now).toISOString().slice(0, 10);

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The rows the grid shows from `date`: seven days, or the 24 hours of that day on a minute World. */
export const rowsFor = (preset: string, date: string): Row[] => {
  if (preset === 'temporal-minute') {
    return Array.from({ length: 24 }, (_, hour) => ({ at: { date, hour }, label: `${String(hour).padStart(2, '0')}:00` }));
  }
  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(date, i);
    return { at: { date: d }, label: `${WEEKDAY[new Date(`${d}T00:00:00Z`).getUTCDay()]} ${d.slice(5)}` };
  });
};

/** How far the arrows move the grid: a week of days, or a day of hours. */
export const stepDays = (preset: string): number => (preset === 'temporal-minute' ? 1 : 7);

/**
 * What one slot is, from where the console stands (acting as `actor`):
 *   mine       the actor holds it
 *   mine-soft  the actor holds it softly, as intent
 *   taken      not offered: others hold every seat of it
 *   shared     offered, though someone holds a seat of it
 *   soft       offered, with someone's soft hold on it
 *   free       offered, and nobody is on it
 */
export type CellState = 'mine' | 'mine-soft' | 'taken' | 'shared' | 'soft' | 'free';

export interface Cell {
  readonly slot: number;
  readonly state: CellState;
  /** Who holds it, and who holds it softly, by name. */
  readonly holders: readonly string[];
  readonly soft: readonly string[];
}

export const cellsOf = (
  leafSlotBits: number,
  offered: readonly number[],
  holdings: readonly Holding[],
  actor: string | null,
): Cell[] => {
  const free = new Set(offered);
  return Array.from({ length: leafSlotBits }, (_, slot) => {
    const holders = holdings.filter((h) => h.heldSlots.includes(slot)).map((h) => h.occupier.name);
    const soft = holdings.filter((h) => h.softSlots.includes(slot)).map((h) => h.occupier.name);
    const state: CellState =
      actor !== null && holders.includes(actor) ? 'mine'
        : actor !== null && soft.includes(actor) ? 'mine-soft'
          : !free.has(slot) ? 'taken'
            : holders.length > 0 ? 'shared'
              : soft.length > 0 ? 'soft'
                : 'free';
    return { slot, state, holders, soft };
  });
};

/** What a click on a cell does, acting as the actor: take what is not yours, give back what is, firm up intent. */
export const clickAct = (state: CellState, alternate: boolean): 'claim' | 'release' | 'confirm' | 'giveUp' =>
  state === 'mine' ? 'release' : state === 'mine-soft' ? (alternate ? 'giveUp' : 'confirm') : 'claim';

/** A position as the API names it back (parametric year, [month 0–11, day, hour?]) → the console's `At`. */
export const atOf = (parametric: number, coordinates: readonly number[]): At => {
  const [month = 0, day = 1, hour] = coordinates;
  const date = `${String(parametric).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return hour === undefined ? { date } : { date, hour };
};

export const atLabel = (at: At): string => (at.hour === undefined ? at.date : `${at.date} ${String(at.hour).padStart(2, '0')}h`);

/** Slot indices as short runs: [9, 10, 11, 14] → "9–11, 14". */
export const runs = (slots: readonly number[]): string => {
  const sorted = [...slots].sort((a, b) => a - b);
  const out: string[] = [];
  for (let i = 0; i < sorted.length;) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j]! + 1) j += 1;
    out.push(i === j ? String(sorted[i]) : `${sorted[i]}–${sorted[j]}`);
    i = j + 1;
  }
  return out.join(', ');
};

/** A hex mask as slot indices: "0x600" → [9, 10]. */
export const maskSlots = (hex: string): number[] => {
  let mask = BigInt(hex);
  const out: number[] = [];
  for (let slot = 0; mask > 0n; slot += 1, mask >>= 1n) if (mask & 1n) out.push(slot);
  return out;
};

/** "9–11, 14" or "9,10,14" → [9, 10, 11, 14]; null when it does not read. */
export const parseSlots = (text: string): number[] | null => {
  const out: number[] = [];
  for (const part of text.split(',').map((p) => p.trim()).filter(Boolean)) {
    const m = /^(\d+)(?:\s*[-–]\s*(\d+))?$/.exec(part);
    if (!m) return null;
    const from = Number(m[1]);
    const to = m[2] === undefined ? from : Number(m[2]);
    if (to < from) return null;
    for (let s = from; s <= to; s += 1) out.push(s);
  }
  return out.length > 0 ? [...new Set(out)] : null;
};
